import { Button, Grid, Paper, Stack, Typography } from '@mui/material'
import React, { useContext } from 'react'

import { CenteredLoading } from '../../components'
import AuthorizeServicesButton from '../../components/CustomerPortal/AuthorizeServicesButton'
import { CustomerAuthorizationTable } from '../../components/CustomerPortal/CustomerAuthorizationTable'
import ShopContactBox from '../../components/CustomerPortal/ShopContactBox'
import ServicesSummaryPanel from '../../components/InspectionRelatedPanel/ServicesSummaryPanel'
import SummaryPanel from '../../components/SharedUI/SummaryPanel/SummaryPanel'
import RepairOrderMediaList from '../../components/SharedUICustomerPortal/RepairOrderMediaList'
import VehicleInfoBoxForCustomer from '../../components/SharedUICustomerPortal/VehicleInfoBoxForCustomer'
import { useTransformServiceItems } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { RepairOrderPortalContext } from '../../providers/RepairOrderPortalProvider/RepairOrderPortalProvider'
import {
  RepairOrderExpanded,
  ServiceEntry,
  ServiceExpanded,
  ShopEntry,
  VehicleEntry
} from '../../sdk'
import EntriesByStatusListContainerForCustomerPortal from './EntriesByStatusListContainerForCustomerPortal'

interface Props {
  showPrice: boolean
  showPartsLabor: boolean
  previousTotal: number | undefined
  repairOrder: RepairOrderExpanded
  shop: ShopEntry
  vehicle: VehicleEntry
  services: ServiceExpanded[]
  handleDeferOrRecommendService: (
    service: ServiceEntry,
    isDeferral: boolean
  ) => Promise<void>
  customerFirstName: string | undefined
}

const InspectionCustomerUI = ({
  showPrice,
  showPartsLabor,
  previousTotal,
  repairOrder,
  shop,
  vehicle,
  services,
  handleDeferOrRecommendService,
  customerFirstName
}: Props) => {
  const { inspection, vehicleImages, media, serviceInventoryParts } =
    useContext(RepairOrderPortalContext)
  const newTotal = repairOrder?.authorizedPricing!.total!

  const transformedServiceItems = useTransformServiceItems(
    services,
    repairOrder?.entry?.info?.isEstimate,
    vehicle?.id,
    serviceInventoryParts
  )

  return (
    <>
      {transformedServiceItems ? (
        <>
          <Typography variant="h1">
            {`Welcome to ${
              showPrice ? 'the Customer Portal' : 'your Inspection Report'
            }${customerFirstName ? ', ' + customerFirstName : ''}!`}
          </Typography>
          <Grid container spacing={1} pt={1}>
            <Grid item xs={12} lg={8}>
              <Stack spacing={1}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    {vehicleImages && (
                      <VehicleInfoBoxForCustomer
                        vehicleImages={vehicleImages}
                        vehicle={vehicle}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ShopContactBox shop={shop} />
                  </Grid>
                  {media && media.length >= 1 && (
                    <Grid item xs={12} md={6}>
                      <RepairOrderMediaList media={media} />
                    </Grid>
                  )}
                </Grid>
                {inspection && (
                  <EntriesByStatusListContainerForCustomerPortal
                    showPrice={showPrice}
                  />
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Stack direction="column" spacing={1}>
                {showPrice ? (
                  <>
                    <Paper variant="outlined" sx={{ padding: 2 }}>
                      <Typography variant="h4">Services</Typography>
                      <CustomerAuthorizationTable
                        serviceItems={transformedServiceItems}
                        previousTotal={previousTotal}
                        newTotal={newTotal}
                        handleDeferService={(entry) =>
                          handleDeferOrRecommendService(entry, true)
                        }
                        handleRecommendService={(entry) =>
                          handleDeferOrRecommendService(entry, false)
                        }
                        breakdownPrices={showPartsLabor}
                      />
                    </Paper>
                    <SummaryPanel repairOrder={repairOrder} readOnly />
                    <AuthorizeServicesButton
                      previousTotal={previousTotal}
                      repairOrder={repairOrder}
                      shopName={shop.info!.name!}
                    />
                  </>
                ) : (
                  <>
                    <ServicesSummaryPanel
                      services={transformedServiceItems}
                      showPrice={false}
                    />
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() =>
                        (window.location.href = `tel:${shop.info?.phone}`)
                      }
                      fullWidth>
                      Call Us
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      ) : (
        <CenteredLoading />
      )}
    </>
  )
}

export default InspectionCustomerUI
